/**
 * Row for a session.
 */
import * as React from 'react';
import { Building2, Calendar, Clock, Repeat2, Users } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { SESSION_RECURRENCE_CONFIG } from '@/components/sessions/session-configs';
import { SessionActions } from './SessionActions';
import { SessionTypeBadge } from './SessionTypeBadge';
import { formatSessionGroupCountLabel, formatSessionTime } from './session-table-utils';
import { useSessionItem, type SessionItemProps } from '@/hooks/useSessionItem';

/** Row for a session. */
export const SessionRow: React.FC<SessionItemProps> = (props) => {
  const { session } = props;
  const state = useSessionItem(props);
  const StatusIcon = state.statusConfig?.icon;
  const groupCountLabel = state.groupDisplay
    ? formatSessionGroupCountLabel(
        state.groupDisplay.groupCount,
        state.groupDisplay.activeGroupCount,
        state.groupDisplay.groupCount - state.groupDisplay.activeGroupCount,
      )
    : '';

  return (
    <TableRow>
      <td>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
              <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
            </div>
            <span className="font-medium text-foreground">
              {format(state.sessionDate, 'EEEE d MMMM yyyy', { locale: fr })}
            </span>
          </div>
          <div className="flex items-center gap-2 ml-10">
            <Clock className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)]" />
            <span className="text-sm text-muted-foreground">
              {formatSessionTime(state.startTime)} – {formatSessionTime(state.endTime)}
            </span>
          </div>
        </div>
      </td>

      <td className="text-center">
        <div className="flex flex-col items-center gap-1.5">
          <SessionTypeBadge type={state.type} size="md" />
          {state.groupDisplay ? (
            <LabelBadge
              label={groupCountLabel}
              icon={Users}
              variant="outlined"
              color="var(--color-1-nuance-1)"
              size="sm"
            />
          ) : null}
          {session?.mode === 'recurring' && session.recurrenceType && (
            <LabelBadge
              label={
                SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.label || session.recurrenceType
              }
              icon={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.icon ?? Repeat2}
              variant={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.badgeVariant ?? 'ghost'}
              color={
                SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.badgeColor ??
                'var(--color-1-nuance-1)'
              }
              size="sm"
            />
          )}
        </div>
      </td>

      <td>
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4" style={{ color: 'var(--color-1-nuance-1)' }} />
          <span className="text-foreground">{state.establishmentLabel}</span>
        </div>
      </td>

      <td>
        {state.statusConfig && (
          <LabelBadge
            label={state.statusConfig.label}
            icon={
              state.statusConfig.isInProgress ? <span className="session-dot-pulse" /> : StatusIcon
            }
            variant={state.statusConfig.badgeVariant}
            color={state.statusConfig.badgeColor}
            size="md"
          />
        )}
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          <SessionActions {...props} state={state} />
        </div>
      </td>
    </TableRow>
  );
};
